import { DivideIcon, PercentIcon, UsersIcon } from 'lucide-react';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  isValidDecimalInput,
  normalizeDecimalInput,
  redistributePercentages,
} from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import { Alert, Avatar, Button, Card } from '@splitfiesta/ui';

import { SharesStepper } from '@/components/common/SharesStepper';
import { PlaceholderBadge } from '@/components/members/PlaceholderBadge';
import { PercentageSlider } from '@/components/ui/PercentageSlider';

import { useGroupWithDashboardQuery } from '@/hooks/queries/useGroups';

import { resolveUserName } from '@/lib/displayName';

import { useAuthStore } from '@/stores/authStore';

type GroupDefaultSplitMethod = 'equal' | 'percentage' | 'shares';

interface GroupDefaultSplitSettingsProps {
  defaultSplitMethod: GroupDefaultSplitMethod;
  setDefaultSplitMethod: (method: GroupDefaultSplitMethod) => void;
  isLoading: boolean;
  isDeleting: boolean;
  groupId?: string;
  defaultSplitConfig?: {
    percentages?: Record<string, number>;
    shares?: Record<string, number>;
  };
  setDefaultSplitConfig?: (config: {
    percentages?: Record<string, number>;
    shares?: Record<string, number>;
  }) => void;
  /** Whether the parent form has loaded data from API (edit mode only) */
  hasParentInitialized?: boolean;
  /** Per-field edit permission flag (undefined = permitted). */
  canEditDefaultSplit?: boolean;
  /** Subtle hint shown beneath locked controls. */
  lockedHint?: string;
}

const splitMethodOptions: {
  value: GroupDefaultSplitMethod;
  icon: typeof DivideIcon;
  labelKey: string;
  descKey: string;
}[] = [
  {
    value: 'equal',
    icon: DivideIcon,
    labelKey: 'expenses:splitMethods.equal',
    descKey: 'expenses:splitMethods.equalDesc',
  },
  {
    value: 'percentage',
    icon: PercentIcon,
    labelKey: 'expenses:splitMethods.percentage',
    descKey: 'expenses:splitMethods.percentageDesc',
  },
  {
    value: 'shares',
    icon: UsersIcon,
    labelKey: 'expenses:splitMethods.shares',
    descKey: 'expenses:splitMethods.sharesDesc',
  },
];

export function GroupDefaultSplitSettings({
  defaultSplitMethod,
  setDefaultSplitMethod,
  isLoading,
  isDeleting,
  groupId,
  defaultSplitConfig,
  setDefaultSplitConfig,
  hasParentInitialized,
  canEditDefaultSplit = true,
  lockedHint,
}: GroupDefaultSplitSettingsProps) {
  const { t } = useTranslation();
  useNamespace('mobile');
  const currentUser = useAuthStore(state => state.user);

  // Fetch members when we have a groupId (edit mode)
  const { data: groupData } = useGroupWithDashboardQuery(
    groupId || '',
    !!groupId
  );

  // Get balances reference for stable memo
  const balances = groupData?.balances;

  // Get members from the dashboard data (edit mode) or use current user (create mode)
  const members = useMemo(() => {
    if (groupId && balances) {
      return balances.map(b => ({
        userId: b.userId,
        name: b.userName || `Member ${b.userId.slice(-4)}`,
        profileImage: b.profileImage,
        isPlaceholder: Boolean(b.isPlaceholder),
      }));
    }
    if (currentUser) {
      return [
        {
          userId: currentUser.id,
          name: currentUser.name,
          profileImage: currentUser.profileImage ?? undefined,
          isPlaceholder: false,
        },
      ];
    }
    return [];
  }, [groupId, balances, currentUser]);

  // Track members that were originally saved in DB (for highlighting "new" members)
  // Using state instead of refs so it can be safely accessed during render
  const [originallySavedShareMembers, setOriginallySavedShareMembers] =
    useState<Set<string>>(
      () =>
        new Set(
          defaultSplitConfig?.shares
            ? Object.keys(defaultSplitConfig.shares)
            : []
        )
    );
  const [
    originallySavedPercentageMembers,
    setOriginallySavedPercentageMembers,
  ] = useState<Set<string>>(
    () =>
      new Set(
        defaultSplitConfig?.percentages
          ? Object.keys(defaultSplitConfig.percentages)
          : []
      )
  );

  // LOCAL state for percentages - persists across method switches
  // Initialize directly from saved config (don't require members to be loaded)
  const [localPercentages, setLocalPercentages] = useState<
    Record<string, number>
  >(() => {
    // Initialize from parent config if available
    if (
      defaultSplitConfig?.percentages &&
      Object.keys(defaultSplitConfig.percentages).length > 0
    ) {
      return { ...defaultSplitConfig.percentages };
    }
    // Create mode: default to 100% for creator (if members already loaded)
    if (!groupId && members.length === 1 && members[0]) {
      return { [members[0].userId]: 100 };
    }
    return {};
  });

  // LOCAL state for shares - persists across method switches
  // Initialize directly from saved config (don't require members to be loaded)
  const [localShares, setLocalShares] = useState<Record<string, number>>(() => {
    // Initialize from parent config if available
    if (
      defaultSplitConfig?.shares &&
      Object.keys(defaultSplitConfig.shares).length > 0
    ) {
      return { ...defaultSplitConfig.shares };
    }
    return {};
  });

  // Track if we've initialized from parent config (to handle async API data loading)
  const hasInitializedFromParentRef = useRef(false);

  // Effect to update local state when parent config arrives from API
  // This handles the case where component mounts before API data loads
  useEffect(() => {
    // Only run in edit mode (groupId is present)
    if (!groupId) return;

    // Skip if we've already initialized
    if (hasInitializedFromParentRef.current) return;

    // Wait for parent to finish loading API data before initializing
    if (!hasParentInitialized) return;

    // Check if parent config has meaningful data (from API)
    const hasPercentages =
      defaultSplitConfig?.percentages &&
      Object.keys(defaultSplitConfig.percentages).length > 0;
    const hasShares =
      defaultSplitConfig?.shares &&
      Object.keys(defaultSplitConfig.shares).length > 0;

    // Defer state updates to avoid calling setState synchronously in effect
    // IMPORTANT: Set hasInitializedFromParentRef INSIDE the timeout to prevent
    // the sync effect from running with stale data before local state is updated
    const timer = setTimeout(() => {
      if (hasPercentages) {
        setLocalPercentages({ ...defaultSplitConfig.percentages! });
        setOriginallySavedPercentageMembers(
          new Set(Object.keys(defaultSplitConfig.percentages!))
        );
      }

      if (hasShares) {
        setLocalShares({ ...defaultSplitConfig.shares! });
        setOriginallySavedShareMembers(
          new Set(Object.keys(defaultSplitConfig.shares!))
        );
      }

      // Mark as initialized AFTER state updates are applied
      hasInitializedFromParentRef.current = true;
    }, 0);

    return () => clearTimeout(timer);
  }, [groupId, defaultSplitConfig, hasParentInitialized]);

  // Local state for percentage editing (click-to-edit pattern)
  const [localPercentageInput, setLocalPercentageInput] = useState<string>('');
  const [editingPercentageFor, setEditingPercentageFor] = useState<
    string | null
  >(null);

  // Compute effective percentages for display: use parent config for members we haven't touched locally
  // Filter to only include current members to prevent stale user IDs (e.g., from Zustand hydration)
  const effectivePercentages = useMemo(() => {
    const result: Record<string, number> = {};
    const memberIds = new Set(members.map(m => m.userId));
    // Add values from local state, but only for current members
    Object.entries(localPercentages).forEach(([userId, value]) => {
      if (memberIds.has(userId)) {
        result[userId] = value;
      }
    });
    // Add defaults for any members not in local state
    members.forEach(m => {
      if (result[m.userId] === undefined) {
        // In create mode with single member, default to 100% (the creator gets full percentage)
        const defaultValue =
          !groupId && members.length === 1
            ? 100
            : (defaultSplitConfig?.percentages?.[m.userId] ?? 0);
        result[m.userId] = defaultValue;
      }
    });
    return result;
  }, [localPercentages, members, defaultSplitConfig?.percentages, groupId]);

  // Compute effective shares for display: use parent config for members we haven't touched locally
  // Filter to only include current members to prevent stale user IDs (e.g., from Zustand hydration)
  const effectiveShares = useMemo(() => {
    const result: Record<string, number> = {};
    const memberIds = new Set(members.map(m => m.userId));
    // Add values from local state, but only for current members
    Object.entries(localShares).forEach(([userId, value]) => {
      if (memberIds.has(userId)) {
        result[userId] = value;
      }
    });
    // Add defaults for any members not in local state
    members.forEach(m => {
      if (result[m.userId] === undefined) {
        // Check if parent config has a value for this member
        result[m.userId] = defaultSplitConfig?.shares?.[m.userId] ?? 1;
      }
    });
    return result;
  }, [localShares, members, defaultSplitConfig?.shares]);

  // Track last synced values to avoid infinite loops
  const lastSyncedRef = useRef<string>('');

  // Sync to parent whenever effective values change (with loop prevention)
  useEffect(() => {
    // In edit mode, don't sync until we've initialized from parent config
    // This prevents overwriting API data with default values
    if (groupId && !hasInitializedFromParentRef.current) {
      return;
    }

    const newValue = JSON.stringify({
      percentages: effectivePercentages,
      shares: effectiveShares,
    });
    // Only sync if values actually changed
    if (newValue !== lastSyncedRef.current) {
      lastSyncedRef.current = newValue;
      setDefaultSplitConfig?.({
        percentages: effectivePercentages,
        shares: effectiveShares,
      });
    }
  }, [groupId, effectivePercentages, effectiveShares, setDefaultSplitConfig]);

  // Calculate total percentage
  const totalPercentage = useMemo(() => {
    return Object.values(effectivePercentages).reduce(
      (sum, val) => sum + (val || 0),
      0
    );
  }, [effectivePercentages]);

  // Handle percentage input change (store raw string for free typing)
  const handlePercentageInputChange = useCallback((value: string) => {
    if (isValidDecimalInput(value)) {
      setLocalPercentageInput(value);
    }
  }, []);

  // Handle slider change for percentage - redistributes others proportionally to keep total at 100%
  const handlePercentageSliderChange = useCallback(
    (userId: string, value: number) => {
      const redistributed = redistributePercentages(
        userId,
        value,
        effectivePercentages
      );
      setLocalPercentages(redistributed);
    },
    [effectivePercentages]
  );

  // Save percentage to parent state - caps so total doesn't exceed 100%
  const handlePercentageSave = useCallback(
    (userId: string) => {
      const numValue = normalizeDecimalInput(localPercentageInput);
      const value = !isNaN(numValue) ? Math.min(100, Math.max(0, numValue)) : 0;
      handlePercentageSliderChange(userId, value);
      setEditingPercentageFor(null);
      setLocalPercentageInput('');
    },
    [localPercentageInput, handlePercentageSliderChange]
  );

  const handleSharesChange = useCallback((userId: string, next: number) => {
    setLocalShares(prev => ({ ...prev, [userId]: next }));
  }, []);

  // Distribute percentages equally
  const distributeEqually = useCallback(() => {
    if (members.length === 0) return;
    const equalPercentage = Math.floor(100 / members.length);
    const remainder = 100 - equalPercentage * members.length;
    const newPercentages: Record<string, number> = {};
    members.forEach((member, index) => {
      newPercentages[member.userId] =
        equalPercentage + (index === 0 ? remainder : 0);
    });
    setLocalPercentages(newPercentages);
  }, [members]);

  // Fill in the rest - distribute remaining percentage to empty members
  const fillInTheRest = useCallback(() => {
    if (members.length === 0 || totalPercentage >= 100) return;
    const remaining = 100 - totalPercentage;
    const emptyMembers = members.filter(m => !effectivePercentages[m.userId]);
    const membersToFill = emptyMembers.length > 0 ? emptyMembers : members;
    const baseAddition = Math.floor(remaining / membersToFill.length);
    const extraRemainder = remaining - baseAddition * membersToFill.length;

    const newPercentages = { ...effectivePercentages };
    membersToFill.forEach((member, index) => {
      const currentValue = effectivePercentages[member.userId] || 0;
      newPercentages[member.userId] =
        currentValue + baseAddition + (index === 0 ? extraRemainder : 0);
    });
    setLocalPercentages(newPercentages);
  }, [members, totalPercentage, effectivePercentages]);

  const canFillInTheRest = totalPercentage < 100 && members.length > 0;
  const disabled = isLoading || isDeleting || !canEditDefaultSplit;
  // In edit mode, wait for parent to initialize before showing member config
  // This prevents showing incorrect default values while API data is loading
  const isReadyToShowConfig = !groupId || hasParentInitialized;
  const showMemberConfig =
    members.length > 0 &&
    isReadyToShowConfig &&
    (defaultSplitMethod === 'percentage' || defaultSplitMethod === 'shares');

  return (
    <Card>
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <DivideIcon className="w-5 h-5 mr-2" />
            {t('groups:sections.defaultSplit')}
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('groups:defaultSplit.description')}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {splitMethodOptions.map(option => {
            const Icon = option.icon;
            const isSelected = defaultSplitMethod === option.value;
            const isOptionDisabled = disabled;

            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setDefaultSplitMethod(option.value)}
                disabled={isOptionDisabled}
                className={`p-4 rounded-lg border-2 text-center transition-all ${
                  isSelected
                    ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                } ${isOptionDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <div className="flex flex-col items-center gap-2">
                  <Icon
                    className={`w-8 h-8 ${isSelected ? 'text-orange-600 dark:text-orange-400' : 'text-gray-500 dark:text-gray-400'}`}
                  />
                  <div>
                    <div
                      className={`font-medium ${isSelected ? 'text-orange-700 dark:text-orange-300' : 'text-gray-900 dark:text-gray-100'}`}
                    >
                      {t(option.labelKey)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {t(option.descKey)}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {!canEditDefaultSplit && lockedHint && (
          <p className="text-xs text-gray-400 dark:text-gray-500">
            {lockedHint}
          </p>
        )}

        {/* Member Configuration for Percentage */}
        {showMemberConfig && defaultSplitMethod === 'percentage' && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="mb-3">
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {t('groups:defaultSplit.memberPercentages')}
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('groups:defaultSplit.percentageHint')}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
              <div className="flex flex-wrap items-center gap-2">
                {groupId && (
                  <Button
                    variant="secondary"
                    size="sm"
                    type="button"
                    onClick={distributeEqually}
                    disabled={disabled}
                    className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 font-medium text-xs sm:text-sm"
                  >
                    {t('groups:defaultSplit.distributeEqually')}
                  </Button>
                )}
                {canFillInTheRest && groupId && (
                  <Button
                    variant="secondary"
                    size="sm"
                    type="button"
                    onClick={fillInTheRest}
                    disabled={disabled}
                    className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 font-medium text-xs sm:text-sm"
                  >
                    {t('expenses:actions.fillInTheRest')}
                  </Button>
                )}
                <span
                  className={`text-sm font-medium whitespace-nowrap ${
                    totalPercentage === 100
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-red-600 dark:text-red-400'
                  }`}
                >
                  {totalPercentage}%
                </span>
              </div>
            </div>

            <div className="space-y-2">
              {members.map(member => {
                const isConfigured = originallySavedPercentageMembers.has(
                  member.userId
                );

                return (
                  <div
                    key={member.userId}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-2 sm:p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50"
                  >
                    <div className="flex items-center gap-2">
                      <Avatar
                        name={member.name}
                        src={member.profileImage}
                        size="sm"
                      />
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {resolveUserName(member.name, t)}
                      </span>
                      {member.isPlaceholder && <PlaceholderBadge />}
                      {groupId && !isConfigured && (
                        <span className="flex-shrink-0 text-xs px-1.5 py-0.5 bg-amber-100 dark:bg-amber-800 text-amber-700 dark:text-amber-200 rounded">
                          {t('common:labels.new')}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 pl-10 sm:pl-0">
                      <PercentageSlider
                        value={effectivePercentages[member.userId] || 0}
                        onChange={value =>
                          handlePercentageSliderChange(member.userId, value)
                        }
                        disabled={disabled}
                        className="sm:flex-none sm:w-28"
                      />
                      {editingPercentageFor === member.userId ? (
                        <input
                          type="text"
                          inputMode="decimal"
                          value={localPercentageInput}
                          onChange={e =>
                            handlePercentageInputChange(e.target.value)
                          }
                          onBlur={() => handlePercentageSave(member.userId)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handlePercentageSave(member.userId);
                            } else if (e.key === 'Escape') {
                              setEditingPercentageFor(null);
                              setLocalPercentageInput('');
                            }
                          }}
                          autoFocus
                          autoComplete="off"
                          placeholder="0"
                          disabled={disabled}
                          className="w-12 px-1 py-1 text-sm text-center border border-orange-400 dark:border-orange-500 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 disabled:opacity-50 focus:ring-2 focus:ring-orange-500"
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setLocalPercentageInput(
                              String(effectivePercentages[member.userId] || '')
                            );
                            setEditingPercentageFor(member.userId);
                          }}
                          disabled={disabled}
                          className="w-12 px-1 py-1 text-sm text-center border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:border-gray-400 dark:hover:border-gray-500 active:bg-gray-100 dark:active:bg-gray-700 transition-colors disabled:opacity-50"
                        >
                          {effectivePercentages[member.userId] || 0}
                        </button>
                      )}
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        %
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {totalPercentage !== 100 && (
              <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-700 dark:text-red-300">
                  {t('groups:defaultSplit.percentageMustEqual100')}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Member Configuration for Shares */}
        {showMemberConfig && defaultSplitMethod === 'shares' && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="mb-3">
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {t('groups:defaultSplit.memberShares')}
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('groups:defaultSplit.sharesHint')}
              </p>
            </div>

            <div className="space-y-2">
              {members.map(member => {
                const isConfigured = originallySavedShareMembers.has(
                  member.userId
                );
                const displayShares = effectiveShares[member.userId] ?? 1;

                return (
                  <div
                    key={member.userId}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-2 sm:p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50"
                  >
                    <div className="flex items-center gap-2">
                      <Avatar
                        name={member.name}
                        src={member.profileImage}
                        size="sm"
                      />
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {resolveUserName(member.name, t)}
                      </span>
                      {member.isPlaceholder && <PlaceholderBadge />}
                      {groupId && !isConfigured && (
                        <span className="flex-shrink-0 text-xs px-1.5 py-0.5 bg-amber-100 dark:bg-amber-800 text-amber-700 dark:text-amber-200 rounded">
                          {t('common:labels.new')}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 pl-10 sm:pl-0">
                      <SharesStepper
                        value={displayShares}
                        onChange={next =>
                          handleSharesChange(member.userId, next)
                        }
                        disabled={disabled}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Info note when using percentage/shares in create mode */}
        {!groupId &&
          (defaultSplitMethod === 'percentage' ||
            defaultSplitMethod === 'shares') && (
            <Alert
              type="info"
              message={t('groups:defaultSplit.addMembersAfterCreate')}
              className="mt-3"
            />
          )}
      </div>
    </Card>
  );
}

export default GroupDefaultSplitSettings;
